import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, FileEdit, PackageX, TriangleAlert, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiDashboard, errMsg } from "../adminApi";

function Kpi({ label, value, icon: Icon, testid, tone = "chalk" }) {
  return (
    <Card className="border-line bg-surface2" data-testid={testid}>
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-steel">{label}</p>
          <p className={`mt-1 font-display text-3xl font-bold tabular-nums text-${tone}`}>{value}</p>
        </div>
        <Icon className="h-6 w-6 text-tertiary" />
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    setError("");
    apiDashboard().then(setData).catch((e) => setError(errMsg(e)));
  };
  useEffect(load, []);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Dashboard</h1>
        <p className="mt-1 text-sm text-steel">Panoramica del catalogo.</p>
      </div>

      {error && (
        <Card className="border-destructive/40 bg-surface2">
          <CardContent className="flex items-center justify-between p-4">
            <p className="text-sm text-chalk">{error}</p>
            <Button variant="secondary" size="sm" onClick={load}>Riprova</Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {!data ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-xl" />)
        ) : (
          <>
            <Kpi label="Pubblicati" value={data.kpis.published} icon={Package} testid="dashboard-kpi-published" />
            <Kpi label="Bozze" value={data.kpis.draft} icon={FileEdit} testid="dashboard-kpi-draft" />
            <Kpi label="Varianti esaurite" value={data.kpis.out_of_stock} icon={PackageX} testid="dashboard-kpi-out-of-stock" />
            <Kpi label="Scorte 1-2 pz" value={data.kpis.low_stock} icon={TriangleAlert} testid="dashboard-kpi-low-stock" />
          </>
        )}
      </div>

      <Card className="border-line bg-surface2">
        <CardHeader>
          <CardTitle className="font-display text-base">Da sistemare</CardTitle>
        </CardHeader>
        <CardContent>
          {!data ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
            </div>
          ) : data.to_fix.length === 0 ? (
            <p className="py-6 text-sm text-steel">Tutto in ordine. Nessun elemento da sistemare.</p>
          ) : (
            <ScrollArea className="max-h-[420px] pr-2">
              <ul className="flex flex-col gap-1" data-testid="dashboard-to-fix-list">
                {data.to_fix.map((item, idx) => (
                  <li key={`${item.product_id}-${idx}`}>
                    <button
                      onClick={() => navigate(`/admin/prodotti/${item.product_id}?tab=${item.tab}`)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors duration-150 hover:bg-surface3"
                      data-testid="dashboard-to-fix-item"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-chalk">{item.name}</span>
                        <span className="block text-xs text-steel">{item.reason}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" />
                    </button>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
